import type { ReactNode } from 'react'

/**
 * The customer app's 404 screen.
 *
 * Legacy carries this markup **twice** — `pages/not-found.tsx` and the miss branch inside
 * `pages/CmsPage.tsx` — identical but for one clause: the CMS page adds *"alebo nie je verejná"*, because
 * an unpublished page is a different miss from a wrong address. That one differing sentence is the only
 * prop; everything else is fixed, so the two screens cannot drift apart the way the originals could.
 *
 * The copy is passed in rather than written here: this package is the design system, and §8 keeps Slovak
 * out of it. The callers resolve their own strings through `T`/`tt`.
 */
export function NotFoundScreen({
  heading,
  explanation,
  homeLabel,
  homeHref = '/',
}: {
  heading: ReactNode
  explanation: ReactNode
  homeLabel: ReactNode
  homeHref?: string
}) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center w-full" data-not-found>
      <div className="text-center space-y-4">
        <h1 className="text-8xl font-display font-bold text-primary">404</h1>
        <h2 className="text-2xl font-bold">{heading}</h2>
        <p className="text-muted-foreground max-w-sm mx-auto">{explanation}</p>
        {/* A plain anchor, not wouter's `Link`: this package has no router, and it is the one place in
            the app where a full reload is harmless - the customer is leaving a page that does not
            exist. The callers that do have a router pass `homeHref` and get the same markup. */}
        <a
          href={homeHref}
          data-to-home
          className="inline-block mt-4 px-6 py-3 bg-foreground text-background font-bold rounded-xl hover:bg-primary hover:text-primary-foreground transition-colors"
        >
          {homeLabel}
        </a>
      </div>
    </div>
  )
}
